import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, DollarSign, Users, Zap, TrendingUp, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const AdminUsagePage = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadStats();
  }, [days]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/usage-stats?days=${days}`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load usage stats');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen theme-bg flex items-center justify-center">
        <RefreshCw className="w-12 h-12 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen theme-bg flex items-center justify-center">
        <p className="text-amber-600">No data available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
              AI Usage Dashboard
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className={`border rounded-lg px-3 py-2 ${isDarkMode ? 'bg-black/50 border-amber-600 text-amber-100' : 'bg-white border-amber-300'}`}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button onClick={loadStats} variant="outline" className={`${isDarkMode ? 'border-amber-600 text-amber-300 hover:bg-amber-900/30' : 'border-amber-300'}`}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className={`card-glow ${isDarkMode ? 'bg-green-900/30 border-green-700' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-green-300' : 'text-green-600'}`}>Total Cost</p>
                  <p className={`text-3xl font-bold ${isDarkMode ? 'text-green-100' : 'text-green-900'}`}>${stats.total_cost.toFixed(2)}</p>
                </div>
                <DollarSign className={`w-10 h-10 opacity-50 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
              </div>
            </CardContent>
          </Card>

          <Card className={`card-glow ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>Total Requests</p>
                  <p className={`text-3xl font-bold ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>{stats.total_requests.toLocaleString()}</p>
                </div>
                <Zap className={`w-10 h-10 opacity-50 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
              </div>
            </CardContent>
          </Card>

          <Card className={`card-glow ${isDarkMode ? 'bg-purple-900/30 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>Avg Cost/Request</p>
                  <p className={`text-3xl font-bold ${isDarkMode ? 'text-purple-100' : 'text-purple-900'}`}>
                    ${stats.total_requests > 0 ? (stats.total_cost / stats.total_requests).toFixed(4) : '0.00'}
                  </p>
                </div>
                <TrendingUp className={`w-10 h-10 opacity-50 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
              </div>
            </CardContent>
          </Card>

          <Card className={`card-glow ${isDarkMode ? 'bg-amber-900/30 border-amber-700' : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>Top Users</p>
                  <p className={`text-3xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>{stats.top_users?.length || 0}</p>
                </div>
                <Users className={`w-10 h-10 opacity-50 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* By Feature */}
          <Card className={`card-glow ${isDarkMode ? 'bg-black/50 border-amber-700' : ''}`}>
            <CardHeader>
              <CardTitle className={isDarkMode ? 'text-amber-100' : 'text-amber-900'}>Usage by Feature</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats.by_feature || {}).map(([feature, data]) => (
                  <div key={feature} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                    <div>
                      <p className={`font-medium capitalize ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>{feature.replace(/_/g, ' ')}</p>
                      <p className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{data.count} requests</p>
                    </div>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>${data.cost.toFixed(2)}</p>
                  </div>
                ))}
                {Object.keys(stats.by_feature || {}).length === 0 && (
                  <p className={`text-center py-4 ${isDarkMode ? 'text-amber-400' : 'text-gray-500'}`}>No usage data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* By Model */}
          <Card className={`card-glow ${isDarkMode ? 'bg-black/50 border-amber-700' : ''}`}>
            <CardHeader>
              <CardTitle className={isDarkMode ? 'text-amber-100' : 'text-amber-900'}>Usage by Model</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats.by_model || {}).map(([model, data]) => (
                  <div key={model} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>{model}</p>
                      <p className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{data.count} requests</p>
                    </div>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>${data.cost.toFixed(2)}</p>
                  </div>
                ))}
                {Object.keys(stats.by_model || {}).length === 0 && (
                  <p className={`text-center py-4 ${isDarkMode ? 'text-blue-400' : 'text-gray-500'}`}>No usage data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Users */}
        <Card className={`card-glow mb-8 ${isDarkMode ? 'bg-black/50 border-amber-700' : ''}`}>
          <CardHeader>
            <CardTitle className={isDarkMode ? 'text-amber-100' : 'text-amber-900'}>Top Users by Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'border-amber-700' : 'border-amber-200'}`}>
                    <th className={`text-left py-3 px-4 font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>User</th>
                    <th className={`text-left py-3 px-4 font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Email</th>
                    <th className={`text-right py-3 px-4 font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Requests</th>
                    <th className={`text-right py-3 px-4 font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.top_users || []).map((user, idx) => (
                    <tr key={user.user_id} className={`border-b ${isDarkMode ? 'border-amber-800 hover:bg-amber-900/20' : 'border-amber-100 hover:bg-amber-50'}`}>
                      <td className={`py-3 px-4 ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>{user.name}</td>
                      <td className={`py-3 px-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{user.email}</td>
                      <td className={`py-3 px-4 text-right ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>{user.request_count}</td>
                      <td className={`py-3 px-4 text-right font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>${user.total_cost.toFixed(2)}</td>
                    </tr>
                  ))}
                  {(stats.top_users || []).length === 0 && (
                    <tr>
                      <td colSpan={4} className={`text-center py-8 ${isDarkMode ? 'text-amber-400' : 'text-gray-500'}`}>No user data yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Daily Breakdown */}
        <Card className={`card-glow ${isDarkMode ? 'bg-black/50 border-amber-700' : ''}`}>
          <CardHeader>
            <CardTitle className={isDarkMode ? 'text-amber-100' : 'text-amber-900'}>Daily Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {(stats.daily || []).map((day) => (
                <div key={day.date} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'hover:bg-amber-900/20' : 'hover:bg-amber-50'}`}>
                  <span className={isDarkMode ? 'text-amber-200' : 'text-amber-800'}>{day.date}</span>
                  <div className="flex items-center space-x-4">
                    <span className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{day.count} requests</span>
                    <span className={`font-semibold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>${day.cost.toFixed(2)}</span>
                  </div>
                </div>
              ))}
              {(stats.daily || []).length === 0 && (
                <p className={`text-center py-4 ${isDarkMode ? 'text-amber-400' : 'text-gray-500'}`}>No daily data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Rate Limits Info */}
        <Card className={`card-glow mt-8 ${isDarkMode ? 'bg-black/50 border-amber-700' : ''}`}>
          <CardHeader>
            <CardTitle className={isDarkMode ? 'text-amber-100' : 'text-amber-900'}>Current Rate Limits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Trial Users (per day)</h3>
                <ul className={`space-y-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  <li>• Course Generation: 2</li>
                  <li>• Chat Messages: 50</li>
                  <li>• Debate Messages: 30</li>
                  <li>• Quiz Generations: 20</li>
                </ul>
              </div>
              <div>
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Paid Users (per day)</h3>
                <ul className={`space-y-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  <li>• Course Generation: 10</li>
                  <li>• Chat Messages: 200</li>
                  <li>• Debate Messages: 100</li>
                  <li>• Quiz Generations: 100</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminUsagePage;
